import { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const { data } = await api.get('/admin/messages');
      setMessages(data.messages);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (msg) => {
    setSelected(msg);
    if (!msg.isRead) {
      try {
        await api.patch(`/admin/messages/${msg.id}/read`);
        setMessages(prev =>
          prev.map(m => m.id === msg.id ? { ...m, isRead: true } : m)
        );
      } catch {
        // silent
      }
    }
  };

  const deleteMessage = async (id) => {
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages(prev => prev.filter(m => m.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Message deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const unreadCount = messages.filter(m => !m.isRead).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Contact Messages</h1>
        <p className="text-sm text-slate-400 mt-1">
          {messages.length} message{messages.length !== 1 ? 's' : ''}
          {unreadCount > 0 && <span className="text-cyan-400"> ({unreadCount} unread)</span>}
        </p>
      </div>

      {messages.length === 0 ? (
        <div className="bg-navy-800 border border-navy-700 rounded-xl p-12 text-center">
          <p className="text-slate-400">No contact messages yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Message list */}
          <div className="space-y-2">
            {messages.map(msg => (
              <button
                key={msg.id}
                onClick={() => markRead(msg)}
                className={`w-full text-left p-4 rounded-xl border transition-colors ${
                  selected?.id === msg.id
                    ? 'bg-cyan-500/10 border-cyan-500/30'
                    : 'bg-navy-800 border-navy-700 hover:border-navy-600'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {!msg.isRead && (
                        <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" />
                      )}
                      <p className={`text-sm truncate ${msg.isRead ? 'text-slate-300' : 'text-white font-semibold'}`}>
                        {msg.name}
                      </p>
                    </div>
                    <p className="text-xs text-slate-400 truncate mt-0.5">{msg.email}</p>
                    <p className="text-xs text-slate-500 mt-1 truncate">{msg.subject}</p>
                  </div>
                  <span className="text-xs text-slate-500 shrink-0">
                    {new Date(msg.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Message detail */}
          <div>
            {selected ? (
              <div className="bg-navy-800 border border-navy-700 rounded-xl p-6 sticky top-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{selected.name}</h3>
                    <a href={`mailto:${selected.email}`} className="text-sm text-cyan-400 hover:underline">
                      {selected.email}
                    </a>
                    {selected.company && (
                      <p className="text-sm text-slate-400 mt-0.5">{selected.company}</p>
                    )}
                  </div>
                  <button
                    onClick={() => deleteMessage(selected.id)}
                    className="text-slate-500 hover:text-red-400 transition-colors p-1"
                    title="Delete message"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <div className="border-t border-navy-700 pt-4 mb-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Subject</p>
                  <p className="text-sm text-slate-300">{selected.subject}</p>
                </div>

                <div className="border-t border-navy-700 pt-4 mb-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Message</p>
                  <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                </div>

                <div className="border-t border-navy-700 pt-4">
                  <p className="text-xs text-slate-500">
                    Received {new Date(selected.createdAt).toLocaleString()}
                  </p>
                </div>

                <div className="mt-4">
                  <a
                    href={`mailto:${selected.email}?subject=Re: ${selected.subject}`}
                    className="inline-flex items-center gap-2 px-4 py-2 btn-gradient text-white text-sm font-medium rounded-lg"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Reply via Email
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-navy-800 border border-navy-700 rounded-xl p-12 text-center">
                <svg className="w-12 h-12 text-slate-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-slate-500 text-sm">Select a message to view</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
